import React, { useState, useEffect, useCallback } from "react";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  approveBankSlipAPI,
  rejectBankSlipAPI,
  getAllBankSlipsAPI,
  getAllSeminarPaymentsAPI,
  approveSeminarPaymentAPI,
  rejectSeminarPaymentAPI,
  getAllCoursePaymentsAPI,
  approveCoursePaymentAPI,
  rejectCoursePaymentAPI,
} from "../../../services/portal/admin/bankTransferService";
import { getClassesAPI } from "../../../services/portal/admin/classService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import {
  formatDate,
  formatAmount,
  tableHeaderColor,
  getSeminarPaymentType,
} from "../../../utility/Common";

const BankTransferManagePage = () => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filteredBankTransfers, setFilteredBankTransfers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("0");
  const [selectedStatus, setSelectedStatus] = useState("1");
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedBankTransfer, setSelectedBankTransfer] = useState(null);

  const [seminarLoading, setSeminarLoading] = useState(false);
  const [seminarSubmitting, setSeminarSubmitting] = useState(false);
  const [seminarPayments, setSeminarPayments] = useState([]);
  const [selectedSeminarStatus, setSelectedSeminarStatus] = useState("1");
  const [showSeminarActionModal, setShowSeminarActionModal] = useState(false);
  const [selectedSeminarPayment, setSelectedSeminarPayment] = useState(null);

  const [courseLoading, setCourseLoading] = useState(false);
  const [courseSubmitting, setCourseSubmitting] = useState(false);
  const [coursePayments, setCoursePayments] = useState([]);
  const [selectedCourseStatus, setSelectedCourseStatus] = useState("1");
  const [showCourseActionModal, setShowCourseActionModal] = useState(false);
  const [selectedCoursePayment, setSelectedCoursePayment] = useState(null);

  useEffect(() => {
    loadClasses();
  }, []);

  const loadBankTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllBankSlipsAPI(selectedClassId, selectedStatus);
      if (res && res.IsSuccessful && res.Data) {
        setFilteredBankTransfers(res.Data || []);
      } else {
        setFilteredBankTransfers([]);
      }
    } catch (error) {
      console.error("Error loading bank transfers:", error);
      showErrorToastMsg("Failed to load bank transfers");
      setFilteredBankTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId, selectedStatus]);

  const loadSeminarPayments = useCallback(async () => {
    setSeminarLoading(true);
    try {
      const res = await getAllSeminarPaymentsAPI(0, selectedSeminarStatus);
      if (res && res.IsSuccessful && res.Data) {
        setSeminarPayments(res.Data || []);
      } else {
        setSeminarPayments([]);
      }
    } catch (error) {
      console.error("Error loading seminar payments:", error);
      showErrorToastMsg("Failed to load seminar payments");
      setSeminarPayments([]);
    } finally {
      setSeminarLoading(false);
    }
  }, [selectedSeminarStatus]);

  const loadCoursePayments = useCallback(async () => {
    setCourseLoading(true);
    try {
      const res = await getAllCoursePaymentsAPI(0, selectedCourseStatus);
      if (res && res.IsSuccessful && res.Data) {
        setCoursePayments(res.Data || []);
      } else {
        setCoursePayments([]);
      }
    } catch (error) {
      console.error("Error loading course payments:", error);
      showErrorToastMsg("Failed to load course payments");
      setCoursePayments([]);
    } finally {
      setCourseLoading(false);
    }
  }, [selectedCourseStatus]);

  useEffect(() => {
    loadBankTransfers();
  }, [loadBankTransfers]);

  useEffect(() => {
    loadSeminarPayments();
  }, [loadSeminarPayments]);

  useEffect(() => {
    loadCoursePayments();
  }, [loadCoursePayments]);

  const loadClasses = async () => {
    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        setClasses(res.Data);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
    }
  };

  const handleClassFilterChange = (e) => {
    setSelectedClassId(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleActionClick = (bankTransfer) => {
    setSelectedBankTransfer(bankTransfer);
    setShowActionModal(true);
  };

  const handleCloseModal = () => {
    setShowActionModal(false);
    setSelectedBankTransfer(null);
  };

  const handleSeminarActionClick = (payment) => {
    setSelectedSeminarPayment(payment);
    setShowSeminarActionModal(true);
  };

  const handleCloseSeminarModal = () => {
    setShowSeminarActionModal(false);
    setSelectedSeminarPayment(null);
  };

  const handleSeminarApprove = async () => {
    setSeminarSubmitting(true);
    try {
      const res = await approveSeminarPaymentAPI(
        selectedSeminarPayment.StudentSeminarAssignId,
      );
      if (res && res.IsSuccessful) {
        showSuccessToastMsg(
          res.Message || "Seminar payment approved successfully",
        );
        handleCloseSeminarModal();
        loadSeminarPayments();
      } else {
        showErrorToastMsg(res?.Message || "Failed to approve seminar payment");
      }
    } catch (error) {
      console.error("Error approving seminar payment:", error);
      showErrorToastMsg("Failed to approve seminar payment");
    } finally {
      setSeminarSubmitting(false);
    }
  };

  const handleSeminarReject = async () => {
    setSeminarSubmitting(true);
    try {
      const res = await rejectSeminarPaymentAPI(selectedSeminarPayment.StudentSeminarAssignId);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg(
          res.Message || "Seminar payment rejected successfully",
        );
        handleCloseSeminarModal();
        loadSeminarPayments();
      } else {
        showErrorToastMsg(res?.Message || "Failed to reject seminar payment");
      }
    } catch (error) {
      console.error("Error rejecting seminar payment:", error);
      showErrorToastMsg("Failed to reject seminar payment");
    } finally {
      setSeminarSubmitting(false);
    }
  };

  const handleCourseActionClick = (payment) => {
    setSelectedCoursePayment(payment);
    setShowCourseActionModal(true);
  };

  const handleCloseCourseModal = () => {
    setShowCourseActionModal(false);
    setSelectedCoursePayment(null);
  };

  const handleCourseApprove = async () => {
    setCourseSubmitting(true);
    try {
      const res = await approveCoursePaymentAPI(selectedCoursePayment.StudentCourseAssignId);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg(res.Message || "Course payment approved successfully");
        handleCloseCourseModal();
        loadCoursePayments();
      } else {
        showErrorToastMsg(res?.Message || "Failed to approve course payment");
      }
    } catch (error) {
      console.error("Error approving course payment:", error);
      showErrorToastMsg("Failed to approve course payment");
    } finally {
      setCourseSubmitting(false);
    }
  };

  const handleCourseReject = async () => {
    setCourseSubmitting(true);
    try {
      const res = await rejectCoursePaymentAPI(selectedCoursePayment.StudentCourseAssignId);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg(res.Message || "Course payment rejected successfully");
        handleCloseCourseModal();
        loadCoursePayments();
      } else {
        showErrorToastMsg(res?.Message || "Failed to reject course payment");
      }
    } catch (error) {
      console.error("Error rejecting course payment:", error);
      showErrorToastMsg("Failed to reject course payment");
    } finally {
      setCourseSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const res = await approveBankSlipAPI(selectedBankTransfer.BankSlipId);

      if (res && res.IsSuccessful) {
        showSuccessToastMsg(
          res.Message || "Bank transfer approved successfully",
        );
        handleCloseModal();
        loadBankTransfers();
      } else {
        showErrorToastMsg(res?.Message || "Failed to approve bank transfer");
      }
    } catch (error) {
      console.error("Error approving bank transfer:", error);
      showErrorToastMsg("Failed to approve bank transfer");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      const res = await rejectBankSlipAPI(selectedBankTransfer.BankSlipId);

      if (res && res.IsSuccessful) {
        showSuccessToastMsg(
          res.Message || "Bank transfer rejected successfully",
        );
        handleCloseModal();
        loadBankTransfers();
      } else {
        showErrorToastMsg(res?.Message || "Failed to reject bank transfer");
      }
    } catch (error) {
      console.error("Error rejecting bank transfer:", error);
      showErrorToastMsg("Failed to reject bank transfer");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusNum = Number(status);
    if (statusNum === 2) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          Approved
        </span>
      );
    } else if (statusNum === 4) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
          Rejected
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
          Pending
        </span>
      );
    }
  };

  return (
    <DashboardLayout
      title="Bank Transfer Management"
      subtitle="View and manage all bank transfers"
    >
      <div>
        {/* Filter Section */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Filter by Class:
              </label>
              <select
                value={selectedClassId}
                onChange={handleClassFilterChange}
                className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
              >
                <option value="0">All Classes</option>
                {classes.map((classItem) => (
                  <option key={classItem.ClassId} value={classItem.ClassId}>
                    {classItem.Name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Filter by Status:
              </label>
              <select
                value={selectedStatus}
                onChange={handleStatusFilterChange}
                className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
              >
                <option value="1">Pending</option>
                <option value="2">Approved</option>
                <option value="4">Rejected</option>
              </select>
            </div>

            <div className="text-sm text-gray-600">
              Showing {filteredBankTransfers.length} transfer(s)
            </div>
          </div>
        </div>

        {/* Bank Transfers Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              Bank Transfers List | ගෙවීම් විස්තර
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Class
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : filteredBankTransfers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      No bank transfers found.
                    </td>
                  </tr>
                ) : (
                  filteredBankTransfers.map((transfer) => {
                    return (
                      <tr
                        key={transfer.BankSlipId}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {transfer.User?.FirstName +
                            " " +
                            transfer.User?.LastName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.Class?.Name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.Date ? formatDate(transfer.Date) : ""}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {transfer.IsFreePass
                            ? "Free Card"
                            : transfer.Amount
                              ? formatAmount(transfer.Amount)
                              : ""}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.Number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {getStatusBadge(transfer.Status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleActionClick(transfer)}
                            className="text-primary-600 hover:text-primary-900 font-medium"
                          >
                            Action
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Seminar Payments Filter */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4 mt-8">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Seminar Payment Status:
              </label>
              <select
                value={selectedSeminarStatus}
                onChange={(e) => setSelectedSeminarStatus(e.target.value)}
                className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
              >
                <option value="1">Pending</option>
                <option value="2">Approved</option>
                <option value="4">Rejected</option>
              </select>
            </div>

            <div className="text-sm text-gray-600">
              Showing {seminarPayments.length} payment(s)
            </div>
          </div>
        </div>

        {/* Seminar Payments Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6" id="seminar-payments">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              Seminar Payment Requests | සම්මන්ත්‍රණ ගෙවීම් විස්තර
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Seminar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {seminarLoading ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : seminarPayments.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      No seminar payment requests found.
                    </td>
                  </tr>
                ) : (
                  seminarPayments.map((payment) => (
                    <tr key={payment.Id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.User?.FirstName + " " + payment.User?.LastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.Seminar?.Title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getSeminarPaymentType(payment.Type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.IsFree
                          ? "Free Card"
                          : payment.Seminar.Fee
                            ? formatAmount(payment.Seminar.Fee)
                            : ""}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.Number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {getStatusBadge(payment.Status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {payment.Type === 1 && (
                          <button
                            onClick={() => handleSeminarActionClick(payment)}
                            className="text-primary-600 hover:text-primary-900 font-medium"
                          >
                            Action
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Course Payments Filter */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4 mt-8">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Course Payment Status:
              </label>
              <select
                value={selectedCourseStatus}
                onChange={(e) => setSelectedCourseStatus(e.target.value)}
                className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
              >
                <option value="1">Pending</option>
                <option value="2">Approved</option>
                <option value="4">Rejected</option>
              </select>
            </div>
            <div className="text-sm text-gray-600">
              Showing {coursePayments.length} payment(s)
            </div>
          </div>
        </div>

        {/* Course Payments Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <div className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}>
            <h4 className="text-md font-medium text-white">
              Course Payment Requests | කෝස ගෙවීම් විස්තර
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Course
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {courseLoading ? (
                  <tr>
                    <td colSpan="7" className="text-sm font-medium px-6 py-4 text-center text-gray-500">
                      <Loader />
                    </td>
                  </tr>
                ) : coursePayments.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-sm font-medium px-6 py-4 text-center text-gray-500">
                      No course payment requests found.
                    </td>
                  </tr>
                ) : (
                  coursePayments.map((payment) => (
                    <tr key={payment.Id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.User?.FirstName + " " + payment.User?.LastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.Course?.Title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getSeminarPaymentType(payment.Type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.IsFree
                          ? "Free Card"
                          : payment.Course?.Fee
                            ? formatAmount(payment.Course.Fee)
                            : ""}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.Number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {getStatusBadge(payment.Status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {payment.Type === 1 && (
                          <button
                            onClick={() => handleCourseActionClick(payment)}
                            className="text-primary-600 hover:text-primary-900 font-medium"
                          >
                            Action
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Seminar Action Modal */}
      {showSeminarActionModal && selectedSeminarPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white rounded-lg max-w-3xl w-full mx-4 my-auto flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Seminar Payment Action
                </h3>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto px-6 py-4 flex-1">
              <div className="mb-6">
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Student:</span>{" "}
                    {selectedSeminarPayment.User?.FirstName +
                      " " +
                      selectedSeminarPayment.User?.LastName}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Seminar:</span>{" "}
                    {selectedSeminarPayment.Seminar?.Title}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Amount:</span>{" "}
                    {selectedSeminarPayment.IsFreePass
                      ? "Free Card"
                      : selectedSeminarPayment.Amount
                        ? formatAmount(selectedSeminarPayment.Amount)
                        : ""}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Receipt Number:</span>{" "}
                    {selectedSeminarPayment.Number}
                  </p>
                </div>

                {/* Bank Slip Image */}
                {selectedSeminarPayment.Image && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Slip Image
                    </label>
                    <div className="border border-gray-300 rounded-lg p-2 bg-gray-50">
                      <img
                        src={
                          selectedSeminarPayment.Image.startsWith("data:")
                            ? selectedSeminarPayment.Image
                            : `data:image/jpeg;base64,${selectedSeminarPayment.Image}`
                        }
                        alt="Bank Slip"
                        className="max-w-full h-auto rounded-md mx-auto"
                        style={{ maxHeight: "500px" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={handleCloseSeminarModal}
                disabled={seminarSubmitting}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSeminarReject}
                disabled={seminarSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                {seminarSubmitting ? "Processing..." : "Reject"}
              </button>
              <button
                onClick={handleSeminarApprove}
                disabled={seminarSubmitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
              >
                {seminarSubmitting ? "Processing..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedBankTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white rounded-lg max-w-3xl w-full mx-4 my-auto flex flex-col max-h-[90vh]">
            {/* Header - Fixed */}
            <div className="flex items-center p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Bank Transfer Action
                </h3>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto px-6 py-4 flex-1">
              <div className="mb-6">
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Student:</span>{" "}
                    {selectedBankTransfer.User?.FirstName +
                      " " +
                      selectedBankTransfer.User?.LastName}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Class:</span>{" "}
                    {selectedBankTransfer.Class?.Name}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Amount:</span>{" "}
                    {selectedBankTransfer.IsFreePass
                      ? "Free Card"
                      : selectedBankTransfer.Amount
                        ? formatAmount(selectedBankTransfer.Amount)
                        : ""}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Date:</span>{" "}
                    {selectedBankTransfer.Date
                      ? formatDate(selectedBankTransfer.Date)
                      : "-"}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Receipt Number:</span>{" "}
                    {selectedBankTransfer.Number}
                  </p>
                </div>

                {/* Bank Slip Image */}
                {selectedBankTransfer.Image && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Slip Image
                    </label>
                    <div className="border border-gray-300 rounded-lg p-2 bg-gray-50">
                      <img
                        src={
                          selectedBankTransfer.Image.startsWith("data:")
                            ? selectedBankTransfer.Image
                            : `data:image/jpeg;base64,${selectedBankTransfer.Image}`
                        }
                        alt="Bank Slip"
                        className="max-w-full h-auto rounded-md mx-auto"
                        style={{ maxHeight: "500px" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer with Buttons - Fixed */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={handleCloseModal}
                disabled={submitting}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={submitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                {submitting ? "Processing..." : "Reject"}
              </button>
              <button
                onClick={handleApprove}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
              >
                {submitting ? "Processing..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Course Action Modal */}
      {showCourseActionModal && selectedCoursePayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white rounded-lg max-w-3xl w-full mx-4 my-auto flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Course Payment Action
                </h3>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto px-6 py-4 flex-1">
              <div className="mb-6">
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Student:</span>{" "}
                    {selectedCoursePayment.User?.FirstName + " " + selectedCoursePayment.User?.LastName}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Course:</span>{" "}
                    {selectedCoursePayment.Course?.Title}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Amount:</span>{" "}
                    {selectedCoursePayment.IsFreePass
                      ? "Free Card"
                      : selectedCoursePayment.Amount
                        ? formatAmount(selectedCoursePayment.Amount)
                        : ""}
                  </p>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Receipt Number:</span>{" "}
                    {selectedCoursePayment.Number}
                  </p>
                </div>

                {/* Bank Slip Image */}
                {selectedCoursePayment.Image && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Slip Image
                    </label>
                    <div className="border border-gray-300 rounded-lg p-2 bg-gray-50">
                      <img
                        src={
                          selectedCoursePayment.Image.startsWith("data:")
                            ? selectedCoursePayment.Image
                            : `data:image/jpeg;base64,${selectedCoursePayment.Image}`
                        }
                        alt="Bank Slip"
                        className="max-w-full h-auto rounded-md mx-auto"
                        style={{ maxHeight: "500px" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={handleCloseCourseModal}
                disabled={courseSubmitting}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCourseReject}
                disabled={courseSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                {courseSubmitting ? "Processing..." : "Reject"}
              </button>
              <button
                onClick={handleCourseApprove}
                disabled={courseSubmitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
              >
                {courseSubmitting ? "Processing..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default BankTransferManagePage;
